import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  GetUserByIdRequest, ICompany, IdentityWebApiClient, IUser, UpdateUserRequest, ListRolesRequest, ListRolesResponse, Role, User, 
  CreateUserRequest, DeleteUserByIdRequest, 
  AssignUserTagRequest,
  AssignUserTagResponse,
  RemoveUserTagRequest,
  RemoveUserTagResponse,
  CompanyType
} from 'identity-webapi-client';
import { CompanyService, AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { Role as RoleEnum, BuilderBundleRoles, SupplierBundleRoles } from 'src/app/shared/models/role.model';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { AspNetEmailValidationRegex, AtLeast8CharsAndAtLeastOneNumberRegex } from 'src/app/shared/constants/email-validation';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ListTagsRequest, ListTagsResponse, ResourceType, SituViewWebApiClient, Tag } from 'situview-webapi-client';
import { MatDialog } from '@angular/material/dialog';
import {
  UserCopyDialogComponent, IUserCopyDialogData, IUserCopyDialogResult
} from '../user-copy-dialog/user-copy-dialog.component';

@Component({
  selector: 'app-user-detail',
  templateUrl: './user-detail.component.html',
  styleUrls: ['./user-detail.component.scss']
})
export class UserDetailComponent extends SituBaseComponent {
  get form() { return this?.userFormGroupControls?.controls; }
  get isUpdate() { return !!this.userIdParam; }
  get submitAction(): SubmitAction { return this.userIdParam ? SubmitAction.Save : SubmitAction.Add; }

  showViewAs: boolean = false;
  userIdParam: string | null = null;
  companies: ICompany[] = [];
  user: IUser | null = null;
  userFormGroupControls: FormGroup | null = null;
  allRoles: Role[] = [];
  adminAssignableRoles: Role[] = [];
  bundleRoles: Role[] = [];
  hidePassword: boolean = true;

  /** The company type of the user being edited, which decides which bundles apply. */
  private get editedCompanyType(): CompanyType | undefined {
    return this.companies.find(c => c.id === this.user?.companyId)?.type;
  }

  /**
   * What a company's own admin may hand out: the existing per-feature roles, plus
   * the department bundles for their kind of company. Any bundle the user already
   * holds stays in the list even if it is the other kind, so switching company
   * never hides a role that is actually assigned.
   */
  public get assignableRoles(): Role[] {
    const type = this.editedCompanyType;
    const wanted: string[] =
      type === CompanyType.Manufacturer ? [...SupplierBundleRoles]
        : type === CompanyType.Builder ? [...BuilderBundleRoles]
          : [];
    const held = this.user?.roles?.map(r => r.id) ?? [];
    return [
      ...this.adminAssignableRoles,
      ...this.bundleRoles.filter(r => wanted.includes(r.name ?? '') || held.includes(r.id)),
    ];
  }

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private companyService: CompanyService,
    private identityWebApiClient: IdentityWebApiClient,
    private situViewApiClient: SituViewWebApiClient,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private userService: UserService,
    private dialog: MatDialog) {
      super(authService, stateService);
      this.userIdParam = this.route.snapshot.paramMap.get('userId');
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.getAllRoles();
    this.setUserDefaults();
    this.setupFormGroup();
    this._subscriptions.push(this.companyService.companies$
      .subscribe(c => {
        this.companies = c.map(x => ({ ...x }));
      }));
    if (this.userIdParam) {
      this.loadUser(this.userIdParam);
    }
    this.loadTags();
  }

  shouldShowViewAs(user: IUser | null): boolean {
    if (!user) {
      return false;
    }

    let isAdmin = user.roles?.some(x => x.name == RoleEnum.SituSystemsAdmin) ?? false;
    let isCurrentUser = user.id == this.authService.getUserId();
    return !!user.isEnabled && !isAdmin && !isCurrentUser && this.authService.hasRole(RoleEnum.SituSystemsAdmin);
  }

  onClickViewAsUser() {
    if (!this.user?.id) {
      return;
    }
    this.userService.viewAsUser(this.user.id);
  }

  onClickCopyUser() {
    if (!this.user?.id) {
      return;
    }

    this.dialog.open(UserCopyDialogComponent, {
      data: {
        userId: this.user.id,
        userName: this.user.userName ?? this.user.email ?? ''
      } as IUserCopyDialogData
    })
    .afterClosed()
    .subscribe((result: IUserCopyDialogResult | undefined) => {
      if (result?.user && result.open) {
        this.router.navigate(['/admin/user', result.user.id]);
      }
    });
  }

  onButtonClick() {
    try {
      this.loading = true;
      if (this.submitAction === SubmitAction.Save) {
        let req = {
          id: this.user?.id,
          companyId: this.user?.companyId,
          email: this.form?.email.value,
          firstName: this.form?.firstName.value,
          lastName: this.form?.lastName.value,
          password: this.form?.password.value === '' ? null : this.form?.password.value,
          isEnabled: this.form?.isEnabled.value,
          lockoutEnabled: this.form?.lockoutEnabled.value,
          roles: this.form?.roles.value.map((x: any) => ({ id: x } as Role)),
        } as UpdateUserRequest;

        const unableToSaveUserMessage = "Unable to save user";
        this.identityWebApiClient.updateUser(req).then(response => {
          if (response?.success) {
            this.showMessage("User saved");
          }
          else {
            this.showMessage(unableToSaveUserMessage);
          }
        }, error => {
          this.showMessage(unableToSaveUserMessage);
        });
      } else {
        let req = {
          companyId: this.user?.companyId,
          email: this.form?.email.value,
          firstName: this.form?.firstName.value,
          lastName: this.form?.lastName.value,
          password: this.form?.password.value === '' ? null : this.form?.password.value,
          isEnabled: this.form?.isEnabled.value,
          lockoutEnabled: this.form?.lockoutEnabled.value,
          roles: this.form?.roles.value.map((x: any) => ({ id: x } as Role)),
        } as CreateUserRequest;
        const unableToCreateUserMessage = "Unable to create user";
        this.identityWebApiClient.createUser(req).then(response => {
          if (response?.success) {
            this.showMessage("User created");
            this.router.navigate(['/admin/user', response?.user?.id]);
          }
          else {
            this.showMessage(unableToCreateUserMessage);
          }
        }, error => {
          this.showMessage(unableToCreateUserMessage);
        });
      }
    }
    finally {
      this.loading = false;
    }
  }

  onDeleteClick() {
    if (confirm("Are you sure you wish to delete this user?")) {
      try {
        this.loading = true;
        let req = new DeleteUserByIdRequest();
        req.id = this.user?.id;
        req.builder = undefined // Pass null in here to use the company for the logged in user

        const unableToDeleteUserMessage = "Unable to delete user";
        this.identityWebApiClient.deleteUserById(req).then(response => {
          if (response?.success) {
            this.showMessage("User Deleted");
          }
          else {
            this.showMessage(unableToDeleteUserMessage);
          }
        }, error => {
          this.showMessage(unableToDeleteUserMessage);
        });
      }
      finally {
        this.loading = false;
        this.navigateToUsers(true);
      }
    }
  }

  //tags
  resourceTypeEnum = ResourceType.Any;
  addTag(tag: Tag) {
    if (tag && this.user) {
      let request = new AssignUserTagRequest();
      request.companyId = this.selectedCompany?.id;
      request.tagId = tag.id;
      request.userId = this.user?.id;
  
      this.identityWebApiClient.assignUserTag(request)
        .then((response: AssignUserTagResponse) => {
          if (response.success && this.user?.tagIds) {
            this.user.tagIds.push(tag.id!);
          }
        });
    }
  }

  removeTag(tag: Tag) {
    if (tag && this.user) {
      let request = new RemoveUserTagRequest();
      request.companyId = this.selectedCompany?.id;
      request.tagId = tag.id;
      request.userId = this.user?.id;
  
      this.identityWebApiClient.removeUserTag(request)
        .then((response: RemoveUserTagResponse) => {
          if (response.success && this.user?.tagIds) {
            this.user.tagIds = this.user.tagIds.filter(id => id !== tag.id);
          }
        });
    }
  }

  tags: Tag[] | undefined;
  private loadTags() {
    let request = new ListTagsRequest();
    request.companyId = this.selectedCompany?.id;
    request.resourceType = this.resourceTypeEnum;

    return this.situViewApiClient.listTags(request)
      .then((response: ListTagsResponse) => {
        if (response.success) {
          this.tags = response.tags;
        }
      });
  }
  //tags

  navigateToUsers(reload: boolean = false) {
    // TODO: Replace this hack with a subscription to a user data service so that everything rebinds whenever data changes.
    if (reload) {
      this.router.navigate([`/admin/userreload`, Date.now()]); // Force the router to reload the user screen
    }
    else {
      this.router.navigate(['/admin/user']);
    }
  }

  private setUserDefaults() {
    this.user = new User();
    this.user.email = '';
    this.user.firstName = '';
    this.user.lastName = '';
    this.user.roles = [];
    this.user.isEnabled = true;
    this.user.lockoutEnabled = false;
  }

  private getAllRoles() {
    this.loading = true;
    let request = new ListRolesRequest();
    this.identityWebApiClient.listRoles(request)
      .then((response: ListRolesResponse) => {
        if (response.roles) {
          this.allRoles = response.roles ?? [];
          this.adminAssignableRoles = response.roles.filter(x => 
            x.name === RoleEnum.User ||
            x.name === RoleEnum.SituView ||
            x.name === RoleEnum.SituFacade ||
            x.name === RoleEnum.SituConnect ||
            // x.name === RoleEnum.SituFurnish ||
            // x.name === RoleEnum.SituPlan ||
            // x.name === RoleEnum.SituRender ||
            // x.name === RoleEnum.SituVr ||
            //x.name === RoleEnum.SituVanity ||
            x.name === RoleEnum.Admin ||
            x.name === RoleEnum.Report ||
            x.name === RoleEnum.Release
          ) ?? [];

          // The access-model bundles say which department someone sits in. Held
          // separately so we can offer a builder's team the builder bundles and a
          // supplier's team the supplier ones, rather than all nine to everybody.
          const bundleNames: string[] = [...BuilderBundleRoles, ...SupplierBundleRoles];
          this.bundleRoles = response.roles.filter(x => bundleNames.includes(x.name ?? '')) ?? [];
        }
        
      })
      .then(() => { this.loading = false });
  }

  private loadUser(userIdParam: string) {
    this.loading = true;
    let request = new GetUserByIdRequest()

    request.id = userIdParam;
    const unableToLoadUserMessage = "Unable to load user";

    return this.identityWebApiClient.getUserById(request).then(response => {
      if (response?.success) {
        this.user = response.user ?? null;
        this.setupFormGroup();
        this.populateFormGroupFromUser();
        this.showViewAs = this.shouldShowViewAs(this.user);
        this.loading = false;
      }
      else {
        this.showMessage(unableToLoadUserMessage);
      }
    }, error => {
      this.showMessage(unableToLoadUserMessage);
      this.loading = false;
    });
  }

  private setupFormGroup() {
    let passwordOptions = [];
    if (this.isUpdate) {
      passwordOptions = [Validators.pattern(AtLeast8CharsAndAtLeastOneNumberRegex)];
    }
    else {
      passwordOptions = [Validators.required, Validators.pattern(AtLeast8CharsAndAtLeastOneNumberRegex)]
    }
    this.userFormGroupControls = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.pattern(AspNetEmailValidationRegex)]),
      firstName: new FormControl(''),
      lastName: new FormControl(''),
      password: new FormControl('', passwordOptions),
      roles: new FormControl([]),
      isEnabled: new FormControl(true),
      lockoutEnabled: new FormControl(false)
    });

    this.userFormGroupControls?.get("email")?.valueChanges.subscribe(x => {
      this.breadcrumbService.setParam('email', x);
    });
  }

  private populateFormGroupFromUser() {
    this.userFormGroupControls?.controls["email"].setValue(this.user?.email);
    this.userFormGroupControls?.controls["firstName"].setValue(this.user?.firstName);
    this.userFormGroupControls?.controls["lastName"].setValue(this.user?.lastName);
    this.userFormGroupControls?.controls["roles"].setValue(this.user?.roles?.map(x => x.id));
    this.userFormGroupControls?.controls["isEnabled"].setValue(this.user?.isEnabled);
    this.userFormGroupControls?.controls["lockoutEnabled"].setValue(this.user?.lockoutEnabled ?? false);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}

enum SubmitAction {
  Save = "Save",
  Add = "Add"
}
